/**
 * Modal pós-OAuth: escolhe conta, pixel e página Meta.
 */
import React, { useMemo } from 'react';
import { AppIcon } from '../../../components/icons/AppIcon';
import { MetaLogoIcon } from '../../../components/icons/CustomIcons';
import { Button } from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Modal from '../../../components/ui/Modal';
import type { MetaAdAccount, MetaPage } from '../../../services/metaService';
import type { MetaSelectionData } from '../types/meta';
import { useMetaIntegration } from '../hooks/useMetaIntegration';

const SELECT_CLASS = 'min-h-11 w-full sm:min-h-9';
const SELECT_WRAP_CLASS = 'min-w-0 w-full';
const FORM_ID = 'meta-integration-form';

interface MetaIntegrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: MetaSelectionData) => void;
    accessToken: string;
    accounts: MetaAdAccount[];
    pages: MetaPage[];
    isLoading?: boolean;
}

const MetaIntegrationModal: React.FC<MetaIntegrationModalProps> = ({
    isOpen,
    onClose,
    onSave,
    accessToken,
    accounts,
    pages,
    isLoading = false,
}) => {
    const {
        selectedAccount,
        selectedPixel,
        selectedPage,
        pixels,
        loadingPixels,
        canSave,
        handleAccountChange,
        handlePixelChange,
        handlePageChange,
    } = useMetaIntegration({ accessToken, accounts, pages });

    const accountOptions = useMemo(
        () =>
            accounts.map((account) => ({
                value: account.id,
                label: `${account.name} (${account.account_id})`,
            })),
        [accounts],
    );

    const pixelOptions = useMemo(
        () =>
            pixels.map((pixel) => ({
                value: pixel.id,
                label: `${pixel.name} (${pixel.id})`,
            })),
        [pixels],
    );

    const pageOptions = useMemo(
        () =>
            pages.map((page) => ({
                value: page.id,
                label: page.name,
            })),
        [pages],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAccount || !selectedPage) return;
        onSave({
            account: selectedAccount,
            pixel: selectedPixel,
            page: selectedPage,
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" preventBackdropClose={isLoading}>
            <Modal.Header onClose={onClose} showCloseButton={!isLoading}>
                Conectar Meta Ads
            </Modal.Header>

            <Modal.Body>
                <div className="flex flex-col gap-6">
                    <header className="flex items-start gap-3">
                        <div
                            className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-info/10 sm:size-10"
                            aria-hidden
                        >
                            <MetaLogoIcon className="size-6" />
                        </div>
                        <p className="text-pretty text-sm text-secondary sm:pt-1">
                            Escolha a conta, o pixel e a página que vão rastrear seus leads de
                            Click-to-WhatsApp.
                        </p>
                    </header>

                    <form
                        id={FORM_ID}
                        className="flex flex-col gap-4"
                        onSubmit={handleSubmit}
                        aria-busy={isLoading || loadingPixels}
                    >
                        <fieldset className="min-w-0 space-y-4 border-0 p-0" disabled={isLoading}>
                            <legend className="sr-only">Recursos da conexão Meta</legend>

                            <Select
                                label="Conta de anúncio (obrigatória)"
                                placeholder="Selecione uma conta"
                                options={accountOptions}
                                value={selectedAccount?.id || ''}
                                onChange={(e) => handleAccountChange(e.target.value)}
                                icon={<AppIcon name="briefcase" className="size-4" aria-hidden />}
                                helperText="De onde vêm suas campanhas."
                                className={SELECT_CLASS}
                                containerClassName={SELECT_WRAP_CLASS}
                            />

                            {loadingPixels ? (
                                <PixelFieldShell>
                                    <div
                                        className="flex min-h-11 items-center gap-2 rounded-lg border border-subtle bg-surface-subtle px-3 text-sm text-secondary sm:min-h-9"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <AppIcon
                                            name="loader"
                                            className="size-4 shrink-0 animate-spin"
                                            aria-hidden
                                        />
                                        Buscando pixels desta conta…
                                    </div>
                                </PixelFieldShell>
                            ) : pixels.length === 0 && selectedAccount ? (
                                <PixelFieldShell>
                                    <aside
                                        className="rounded-lg border border-warning/20 bg-warning/10 px-3 py-3 text-sm text-warning"
                                        role="status"
                                    >
                                        <p className="font-medium">Nenhum pixel nesta conta</p>
                                        <p className="mt-1 text-warning/90">
                                            Crie um no Gerenciador de Eventos do Meta e tente de novo.
                                            Você ainda pode conectar sem pixel.
                                        </p>
                                    </aside>
                                </PixelFieldShell>
                            ) : (
                                <Select
                                    label="Pixel do Meta (recomendado)"
                                    placeholder={
                                        !selectedAccount
                                            ? 'Selecione uma conta primeiro'
                                            : 'Selecione um pixel'
                                    }
                                    options={pixelOptions}
                                    value={selectedPixel?.id || ''}
                                    onChange={(e) => handlePixelChange(e.target.value)}
                                    disabled={!selectedAccount || pixels.length === 0}
                                    icon={<AppIcon name="activity" className="size-4" aria-hidden />}
                                    helperText="Usado para enviar conversões de volta ao Meta."
                                    className={SELECT_CLASS}
                                    containerClassName={SELECT_WRAP_CLASS}
                                />
                            )}

                            <Select
                                label="Página do Facebook (obrigatória)"
                                placeholder="Selecione uma página"
                                options={pageOptions}
                                value={selectedPage?.id || ''}
                                onChange={(e) => handlePageChange(e.target.value)}
                                icon={<AppIcon name="file-text" className="size-4" aria-hidden />}
                                helperText={
                                    pages.length === 0
                                        ? undefined
                                        : 'Necessária para anúncios Click-to-WhatsApp.'
                                }
                                error={
                                    pages.length === 0
                                        ? 'Nenhuma página encontrada. Você precisa ser administrador da página no Facebook.'
                                        : undefined
                                }
                                className={SELECT_CLASS}
                                containerClassName={SELECT_WRAP_CLASS}
                            />
                        </fieldset>

                        <aside className="rounded-xl border border-info/20 bg-info/10 p-3 sm:p-4">
                            <div className="flex items-start gap-3">
                                <AppIcon
                                    name="info"
                                    className="mt-0.5 size-5 shrink-0 text-info"
                                    aria-hidden
                                />
                                <div className="min-w-0 text-sm text-info">
                                    <p className="font-medium">O que você ativa</p>
                                    <ul className="mt-2 list-disc space-y-1.5 pl-4 text-info/85">
                                        <li>Saber de qual campanha veio cada lead no WhatsApp</li>
                                        <li>Usar o pixel e a página escolhidos aqui</li>
                                        <li>Enviar eventos de lead e venda de volta para o Meta</li>
                                    </ul>
                                </div>
                            </div>
                        </aside>
                    </form>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button
                    type="button"
                    variant="secondary"
                    className="min-h-11 w-full touch-manipulation sm:min-h-9 sm:w-auto"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    form={FORM_ID}
                    className="min-h-11 w-full min-w-[9rem] touch-manipulation sm:min-h-9 sm:w-auto"
                    disabled={!canSave || isLoading}
                >
                    {isLoading ? (
                        <>
                            <AppIcon name="loader" className="size-4 animate-spin" aria-hidden />
                            Conectando…
                        </>
                    ) : (
                        <>
                            <AppIcon name="check" className="size-4" aria-hidden />
                            Conectar
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/** Label compartilhado dos estados de pixel (loading / vazio) — no mesmo arquivo, sem nano-módulo. */
function PixelFieldShell({ children }: { children: React.ReactNode }) {
    return (
        <div className="grid gap-1.5">
            <p className="text-sm font-medium text-default">
                Pixel do Meta <span className="font-normal text-muted">(recomendado)</span>
            </p>
            {children}
        </div>
    );
}

export default MetaIntegrationModal;
